import analyticsService from '@lib/analytics/AnalyticsService'
import type { SessionStore } from '@lib/store/session'
import { captureException } from '@sentry/react'
import type { IReactionOptions, IReactionPublic } from 'mobx'
import {
    action,
    autorun,
    computed,
    makeObservable,
    observable,
    reaction,
    runInAction,
} from 'mobx'
import type { useRouter } from 'next/router'

import type { OnboardingStore } from './OnboardingStore'

export abstract class BaseViewModel {
    // Common loading and error states
    loading = false
    isSubmitting = false
    errorMessage = ''

    // Navigation state
    protected router: ReturnType<typeof useRouter>
    protected searchParams: URLSearchParams
    protected onboarding: OnboardingStore
    protected sessionStore: SessionStore

    /** Internal array of disposer functions for cleanup */
    private _disposers: Array<() => void> = []

    protected constructor(
        router: ReturnType<typeof useRouter>,
        searchParams: URLSearchParams,
        onboarding: OnboardingStore,
        sessionStore: SessionStore,
    ) {
        this.router = router
        this.searchParams = searchParams
        this.onboarding = onboarding
        this.sessionStore = sessionStore

        makeObservable(this, {
            loading: observable,
            isSubmitting: observable,
            errorMessage: observable,
            setLoading: action,
            setSubmitting: action,
            setError: action,
            clearError: action,
            hasError: computed,
        })
    }

    // Common computed properties
    get hasError(): boolean {
        return this.errorMessage !== ''
    }

    // Common actions for loading and error states
    setLoading(loading: boolean) {
        this.loading = loading
    }

    setSubmitting(submitting: boolean) {
        this.isSubmitting = submitting
    }

    setError(error: string) {
        this.errorMessage = error
    }

    clearError() {
        this.errorMessage = ''
    }

    // =====================
    // Disposal Management
    // =====================

    /**
     * Register a disposer function to be called on dispose().
     * Use this for any cleanup that needs to happen when the ViewModel is destroyed.
     */
    addDisposer(disposer: () => void): void {
        this._disposers.push(disposer)
    }

    /**
     * Create a MobX autorun that is automatically disposed when the ViewModel is disposed.
     * Use this instead of calling autorun() directly.
     */
    addAutorun(view: () => void): void {
        const disposer = autorun(view)
        this._disposers.push(disposer)
    }

    /**
     * Create a MobX reaction that is automatically disposed when the ViewModel is disposed.
     * Use this instead of calling reaction() directly.
     */
    addReaction<T, FireImmediately extends boolean = false>(
        expression: (r: IReactionPublic) => T,
        effect: (
            arg: T,
            prev: FireImmediately extends true ? T | undefined : T,
            r: IReactionPublic,
        ) => void,
        opts?: IReactionOptions<T, FireImmediately>,
    ): void {
        const disposer = reaction(expression, effect, opts)
        this._disposers.push(disposer)
    }

    /**
     * Create a setInterval that is automatically cleared when the ViewModel is disposed.
     * Use this instead of calling setInterval() directly.
     * @returns The interval ID for manual clearing if needed
     */
    addInterval(callback: () => void, ms: number): NodeJS.Timeout {
        const interval = setInterval(callback, ms)
        this._disposers.push(() => clearInterval(interval))
        return interval
    }

    /**
     * Create a setTimeout that is automatically cleared when the ViewModel is disposed.
     * Use this instead of calling setTimeout() directly.
     * @returns The timeout ID for manual clearing if needed
     */
    addTimeout(callback: () => void, ms: number): NodeJS.Timeout {
        const timeout = setTimeout(callback, ms)
        this._disposers.push(() => clearTimeout(timeout))
        return timeout
    }

    /**
     * Cleanup method that disposes all registered disposers.
     * Called automatically by useViewModel hook on unmount.
     * Subclasses can override but should call super.dispose().
     */
    dispose(): void {
        for (const disposer of this._disposers) {
            try {
                disposer()
            } catch (error) {
                console.error('Error during ViewModel disposal:', error)
            }
        }
        this._disposers = []
    }

    // Navigation utilities
    protected async navigate(url: string, options?: { scroll?: boolean }) {
        // Check if we're already on the target URL (comparing just the pathname)
        // to avoid unnecessary navigations that would strip query parameters
        const currentPath = this.router.pathname
        const targetPath = url.startsWith('/') ? url.split('?')[0] : url

        if (currentPath === targetPath) {
            // Already on the target screen, don't navigate
            return
        }

        await this.router.replace(url, undefined, options)
    }

    protected getQueryParam(key: string): string | null {
        return this.searchParams.get(key)
    }

    // Onboarding utilities
    protected setHeaderText(text: string) {
        this.onboarding.setHeaderText(text)
    }

    protected setHeaderBackButtonVisible(visible: boolean) {
        this.onboarding.setHeaderBackButtonVisible(visible)
    }

    protected setBackButtonFunction(fn: () => Promise<void>) {
        this.onboarding.setBackButtonFn(fn)
    }

    // Analytics utilities
    protected async trackEvent(
        eventName: string,
        properties?: Record<
            string,
            | string
            | boolean
            | number
            | Record<string, string>
            | undefined
            | null
        >,
    ): Promise<void> {
        try {
            await analyticsService.trackEvent(eventName, properties)
        } catch (error) {
            captureException(error, {
                tags: {
                    analytics: 'track_event',
                    event_name: eventName,
                },
                level: 'warning',
            })
        }
    }

    // Error handling utilities
    protected handleError(error: unknown, context?: string): void {
        const errorMessage =
            error instanceof Error ? error.message : 'An unknown error occurred'
        const contextualMessage = context
            ? `${context}: ${errorMessage}`
            : errorMessage

        runInAction(() => {
            this.setError(contextualMessage)
            this.setLoading(false)
            this.setSubmitting(false)
        })

        console.error(contextualMessage, error)
    }

    protected async executeWithErrorHandling<T>(
        operation: () => Promise<T>,
        context?: string,
        loadingState: 'loading' | 'submitting' = 'loading',
    ): Promise<T | null> {
        try {
            this.clearError()
            if (loadingState === 'loading') {
                this.setLoading(true)
            } else {
                this.setSubmitting(true)
            }

            const result = await operation()

            runInAction(() => {
                if (loadingState === 'loading') {
                    this.setLoading(false)
                } else {
                    this.setSubmitting(false)
                }
            })

            return result
        } catch (error) {
            this.handleError(error, context)
            return null
        }
    }
}
